'use client';

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { cn, Button, LucideIcon } from '@skin-seoul/design-system';
import {
  CART_QUERY_KEYS,
  createCheckoutContextFromCartResponse,
  formatCartCurrency,
  getCartItemQuantityBounds,
  getProductEffectivePrice,
  useAnalyticsEvent,
  useCartService,
  type CartResponseResponse,
  type Currency,
  type SimpleProduct,
  type VariationProduct,
} from '@skin-seoul/service';
import { BridgeApi, formatReviewRating, useMutationLoading } from '@skin-seoul/utils';
import { CartAnalyticsImpression, useCartAnalytics, type CartCardAttribution } from './CartAnalytics';

type AddTarget = SimpleProduct | VariationProduct;

interface CartAddToBag {
  currency: Currency;
  /** Latest bag response; an add compares it with its own response for tier analytics. */
  cart: CartResponseResponse['cart'] | undefined;
  /** First bag line per SKU with its effective quantity limit. */
  lineBySku: ReadonlyMap<string, { quantity: number; max: number }>;
  /** The bag is loading, errored, or a cart write is in flight. */
  isCartBusy: boolean;
  addToCart: (sku: string) => Promise<CartResponseResponse>;
}

/** One cart subscription for every card in the rails instead of one per card. */
export function useCartAddToBag(currency: Currency): CartAddToBag {
  const { useAddToCart, useGetCartQuery } = useCartService();
  const { mutateAsync } = useAddToCart({ currency });
  const { data, isFetching, isError, isLoading } = useGetCartQuery({ currency });
  const isCartMutating = useMutationLoading(CART_QUERY_KEYS.default());
  const lineBySku = useMemo(() => {
    const lines = new Map<string, { quantity: number; max: number }>();
    data?.cart.item.forEach((item) => {
      if (lines.has(item.sku)) {
        return;
      }
      lines.set(item.sku, { quantity: item.quantity, max: getCartItemQuantityBounds(item).max });
    });
    return lines;
  }, [data]);
  const addToCart = useCallback((sku: string) => mutateAsync({ sku, quantity: 1, currency }), [mutateAsync, currency]);
  const isCartBusy = isFetching || isError || isLoading || isCartMutating;
  return useMemo(
    () => ({ currency, cart: data?.cart, lineBySku, isCartBusy, addToCart }),
    [currency, data, lineBySku, isCartBusy, addToCart],
  );
}

const CartAddToBagContext = createContext<CartAddToBag | null>(null);

/** Mount once above the discovery rails so their cards share one cart subscription. */
export function CartAddToBagProvider({ currency, children }: { currency: Currency; children: ReactNode }) {
  const bag = useCartAddToBag(currency);
  return <CartAddToBagContext.Provider value={bag}>{children}</CartAddToBagContext.Provider>;
}

function Root({
  children,
  className,
  onImpression,
  attribution,
  impressionKey,
}: {
  children: ReactNode;
  className?: string;
  onImpression?: () => void;
  attribution?: CartCardAttribution;
  impressionKey?: string;
}) {
  const content = <article className={cn('w-[120px] shrink-0 snap-start', className)}>{children}</article>;
  return onImpression ? (
    <CartAnalyticsImpression onImpression={onImpression} isDetail={attribution?.isDetail} impressionKey={impressionKey}>
      {content}
    </CartAnalyticsImpression>
  ) : (
    content
  );
}
function Media({ children }: { children: ReactNode }) {
  return <div className="relative">{children}</div>;
}
function Picture({ src, name, className }: { src: string; name: string; className?: string }) {
  return (
    <img
      src={src}
      alt={name}
      width={85}
      height={110}
      loading="lazy"
      className={cn('relative -top-1 h-[110px] w-[85px] object-contain mix-blend-multiply', className)}
    />
  );
}
function Name({
  brand,
  name,
  packLabel,
  layout = 'compact',
}: {
  brand: string;
  name: string;
  packLabel?: string | null;
  layout?: 'compact' | 'grid';
}) {
  return (
    <span
      className={cn(
        'flex w-full min-w-0 flex-col gap-0.5 text-label-small',
        layout === 'grid' && 'gap-1 text-label-medium',
      )}
    >
      <span className={cn('w-full font-medium truncate text-neutral-700', layout === 'grid' && 'font-semibold')}>
        {brand}
      </span>
      <span className={cn('block w-full text-neutral-500', layout === 'grid' ? 'h-10 leading-5' : 'h-8 leading-4')}>
        <span className={packLabel ? 'block truncate' : 'line-clamp-2'}>{name}</span>
        {packLabel && <span className="block truncate text-neutral-700">{packLabel}</span>}
      </span>
    </span>
  );
}
function Price({
  currency,
  current,
  original,
  layout = 'compact',
}: {
  currency: Currency;
  current: number;
  original: number;
  layout?: 'compact' | 'grid';
}) {
  return (
    <span
      className={cn(
        'flex w-full min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-1',
        layout === 'grid' && 'flex-col gap-2',
      )}
    >
      <span
        className={cn(
          'text-label-medium font-semibold break-words text-neutral-700',
          layout === 'grid' && 'text-paragraph-large text-neutral-900',
        )}
      >
        {formatCartCurrency(currency, current)}
      </span>
      {current < original && (
        <span
          className={cn(
            'text-label-small line-through break-words text-neutral-400',
            layout === 'grid' && 'text-label-medium',
          )}
        >
          {formatCartCurrency(currency, original)}
        </span>
      )}
    </span>
  );
}
function Discount({ current, original }: { current: number; original: number }) {
  const hasDiscount = current < original && original > 0;
  if (!hasDiscount) {
    return null;
  }
  return (
    <span className="pointer-events-none absolute left-0 top-0 inline-flex h-5 items-center rounded-tl-lg rounded-br-lg bg-primary-400 px-1.5 text-label-small font-medium text-white">
      {Math.round((1 - current / original) * 100)}% off
    </span>
  );
}
function Rating({
  rating,
  layout = 'compact',
}: {
  rating?: { averageRate: number; totalReviews: number };
  layout?: 'compact' | 'grid';
}) {
  if (!rating || rating.averageRate <= 0) {
    return null;
  }
  return (
    <span
      className={cn(
        'flex items-center gap-1 text-label-small text-neutral-700',
        layout === 'grid' && 'text-label-medium',
      )}
    >
      <LucideIcon name="Star" size={layout === 'grid' ? 16 : 12} className="fill-neutral-900 text-neutral-700" />
      {formatReviewRating(rating.averageRate)} <span className="text-neutral-400">({rating.totalReviews})</span>
    </span>
  );
}

interface AddProps {
  className?: string;
  target: AddTarget | null;
  currency: Currency;
  onCartMutation?: (mutation: Promise<boolean>) => void;
  attribution?: CartCardAttribution;
  analyticsParams?: Record<string, unknown>;
}

function AddButton({
  target,
  currency,
  onCartMutation,
  className,
  attribution,
  analyticsParams,
  bag,
}: AddProps & { bag: CartAddToBag }) {
  const inFlight = useRef(false);
  const [isAdding, setIsAdding] = useState(false);
  const [hasError, setHasError] = useState(false);
  const analytics = useAnalyticsEvent();
  const cartAnalytics = useCartAnalytics();
  const line = target ? bag.lineBySku.get(target.sku) : undefined;
  const isAtLimit = line !== undefined && line.quantity >= line.max;
  const disabled = !target || isAdding || bag.isCartBusy || isAtLimit;
  const add = () => {
    if (disabled || inFlight.current || !target) {
      return;
    }
    inFlight.current = true;
    setIsAdding(true);
    setHasError(false);
    const price = getProductEffectivePrice(target);
    const gtm = {
      'x-fb-cd-content_type': 'product',
      'x-fb-cd-content_name': target.name,
      'x-fb-cd-content_ids': String(target.productId),
      value: String(price),
      currency,
      items: [
        {
          item_id: String(target.productId),
          item_name: target.name,
          item_brand: target.brand,
          item_sku: target.sku,
          price: String(price),
          item_category: target.category?.[0] ?? '',
          item_category2: target.category?.[1] ?? '',
        },
      ],
      event_name: 'add_to_cart',
      event_time: Math.round(Date.now() / 1000),
      product_sku: target.sku,
    };
    const before = bag.cart;
    const mutation = bag
      .addToCart(target.sku)
      .then(
        (response) => {
          analytics.trackAddToCart({
            productId: String(target.productId),
            quantity: 1,
            sectionName: attribution?.placement,
            position: attribution?.position,
            params: analyticsParams,
            checkout: createCheckoutContextFromCartResponse(response),
            gtm,
          });
          cartAnalytics.mutation(
            before,
            response.cart,
            attribution?.placement ?? 'cart',
            attribution?.placement === 'unlock_reward_page' ? 'unlock_reward_page' : 'cart_drawer',
          );
          if (BridgeApi.isWebview()) {
            BridgeApi.cartUpdated();
          }
          return true;
        },
        () => {
          setHasError(true);
          return false;
        },
      )
      .finally(() => {
        inFlight.current = false;
        setIsAdding(false);
      });
    onCartMutation?.(mutation);
  };
  return (
    <>
      {hasError && (
        <span
          role="alert"
          className="absolute inset-x-1 bottom-10 rounded bg-white p-1 text-label-xsmall text-error-500"
        >
          Could not add. Try again.
        </span>
      )}
      <Button
        type="button"
        variant="primary"
        disabled={disabled}
        aria-label="Add to bag"
        aria-busy={isAdding}
        onClick={add}
        className={cn(
          'absolute bottom-1 right-1 size-7 min-w-0 rounded-full bg-primary-400 p-0 [&_svg]:w-4 [&_svg]:h-4 shadow-[0_4px_4px_rgba(111,63,215,0.25)]',
          className,
        )}
      >
        <LucideIcon name="Plus" size={16} className="text-white" />
      </Button>
    </>
  );
}

function StandaloneAdd(props: AddProps) {
  const bag = useCartAddToBag(props.currency);
  return <AddButton {...props} bag={bag} />;
}

function Add(props: AddProps) {
  // Rails rendered without the drawer (tests, embedded cart) have no provider and subscribe per card.
  const bag = useContext(CartAddToBagContext);
  if (bag && bag.currency === props.currency) {
    return <AddButton {...props} bag={bag} />;
  }
  return <StandaloneAdd {...props} />;
}

export const CartDiscoveryCard = Object.assign(Root, { Media, Picture, Name, Price, Discount, Rating, Add });
